import { IconButton, Tooltip } from '@assembly-js/design-system';
import { useMediaQuery, useTheme } from '@mui/material';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { BackIcon } from 'src/components/icons';
import { PLAN_PAGE } from 'src/constants';
import useNavigate from 'src/hooks/useNavigate';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import Button from 'src/legacy/components/Button';
import { typography12MediumStyle } from 'src/legacy/components/Text';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { RootState } from 'src/store';
import { BlackHeadings } from 'src/theme/colors';

const useStyles = makeStyles()(() => ({
  selectPlanButton: {
    color: BlackHeadings,
    textDecoration: 'underline',
    ...typography12MediumStyle,
  },
}));

export const SettingsSidebarBackToggle = () => {
  const { classes } = useStyles();
  const { navigate } = useNavigate();
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const backToPortalDestinationPath = useSelector(
    (state: RootState) => state.ui.backToPortalDestinationPath,
  );
  const { isTrialPeriodExpired, isCancelledPlan, isPaidPlan } = usePlanStatus();

  const handleBackToggleClicked = () => {
    navigate(backToPortalDestinationPath);
  };

  // disable back toggle on settings screen
  const disableBackToggle =
    (isTrialPeriodExpired || isCancelledPlan) && !isPaidPlan;
  const backToggleRenderer = () => {
    if (isSmScreen) {
      return (
        <IconButton
          icon="ChevronLeft"
          disabled={disableBackToggle}
          variant="minimal"
          onClick={handleBackToggleClicked}
          size="sm"
        />
      );
    }

    return (
      <Button
        disabled={disableBackToggle}
        htmlId="settings-sidebar-back-toggle"
        startIcon={<BackIcon />}
        variant="text"
        color="primary"
        onClick={handleBackToggleClicked}
      >
        <BaseTypography fontType="13Medium">Go back</BaseTypography>
      </Button>
    );
  };

  const goToPlanSettings = () => {
    navigate(PLAN_PAGE.path);
  };

  return disableBackToggle ? (
    <Tooltip
      offset={{ x: 16 }}
      position="bottom"
      content={
        <BaseTypography fontType="12Medium" style={{ color: BlackHeadings }}>
          <Trans
            i18nKey="messages.sidebar_back_disabled"
            components={{
              Button: (
                <Button
                  htmlId="go-to-select-plan-btn"
                  variant="text"
                  onClick={goToPlanSettings}
                  className={classes.selectPlanButton}
                />
              ),
            }}
          />
        </BaseTypography>
      }
    >
      <div>{backToggleRenderer()}</div>
    </Tooltip>
  ) : (
    backToggleRenderer()
  );
};
